import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import Tabs from '../../../components/tabs';
import CustomButton from '../../../components/customButton';
// no dispatch needed in modal
import Details from '../../../components/deviceDetails/details';
import ManageZones from '../../../components/manageZones';

const DetailsModal = ({ device, options, isOpen, toggle, onConfirm, onCancel }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [sections, setSections] = useState([]);
  const loadingSubmit = false;

  const onConfigChanged = response => {
    onConfirm(response);
    toggle();
  };
  const canConfirm = () => {};

  function mapStringToCategory(solution) {
    const input = (solution || '').toLowerCase();
    const audienceKeywords = ['face', 'crowd', 'audience'];

    if (audienceKeywords.some(keyword => input.includes(keyword))) {
      return 'Audience';
    }

    if (input.includes('vehicle')) {
      return 'Vehicle';
    }

    return 'Unknown';
  }

  // Map of save functions registered by each Details instance (keyed by tab index)
  const saveFunctionsRef = React.useRef({});
  const [changesMap, setChangesMap] = React.useState({});

  const registerSaveForIndex = index => registrationFactory => {
    const notify = changed => {
      setChangesMap(prev => ({ ...prev, [index]: !!changed }));
    };

    // registrationFactory should be a function that receives notify and returns a registration object
    let reg = registrationFactory;
    if (typeof registrationFactory === 'function') {
      reg = registrationFactory(notify) || {};
    }

    saveFunctionsRef.current[index] = reg;
    // initialize map entry if registration provided has a hasChanges function
    if (reg && typeof reg.hasChanges === 'function') {
      notify(reg.hasChanges());
    }

    return () => {
      delete saveFunctionsRef.current[index];
      setChangesMap(prev => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });
    };
  };

  useEffect(() => {
    setSections(() => {
      if (!options) return [];
      return [
        {
          title: 'Details',
          content: (
            <Details
              sections={['details']}
              renderOnSidebar
              currentPresetId={device.config.selectedSolutionConfigId}
              onConfigChanged={onConfigChanged}
              registerSave={registerSaveForIndex(0)}
              device={
                device
                  ? {
                      ...device,
                      config: options.solutionConfig,
                      defaultConfig: options.defaultConfig
                    }
                  : {}
              }
            />
          )
        },
        {
          title: 'General',
          content: (
            <Details
              sections={['general']}
              renderOnSidebar
              currentPresetId={device.config.selectedSolutionConfigId}
              onConfigChanged={onConfigChanged}
              isAdminUser={options.isAdminUser}
              registerSave={registerSaveForIndex(1)}
              device={
                device
                  ? {
                      ...device,
                      config: options.solutionConfig,
                      defaultConfig: options.defaultConfig
                    }
                  : {}
              }
            />
          )
        },
        {
          title: `${device.config ? mapStringToCategory(device.config.solutionType) : 'Loading...'}`,
          content: (
            <Details
              sections={['advanced']}
              renderOnSidebar
              currentPresetId={device.config.selectedSolutionConfigId}
              onConfigChanged={onConfigChanged}
              isAdminUser={options.isAdminUser}
              registerSave={registerSaveForIndex(2)}
              device={
                device
                  ? {
                      ...device,
                      config: options.solutionConfig,
                      defaultConfig: options.defaultConfig
                    }
                  : {}
              }
            />
          )
        },
        {
          title: 'Zones and ROIs',
          content: (
            <ManageZones
              device={{
                ...device,
                config: options.solutionConfig,
                defaultConfig: options.defaultConfig
              }}
              onConfigChanged={onConfigChanged}
              unlimitedZones={options.isAdminUser}
            />
          )
        }
      ];
    });
  }, [device, options]);

  const getPagesFromSteps = steps => {
    return steps.map(step => {
      const newStep = { ...step, name: step.title };
      newStep.content = (
        <div>
          <h2 className="section-header">
            Editing {newStep.title.toLowerCase()} on {device.displayName}
          </h2>{' '}
          {newStep.content}
        </div>
      );
      delete newStep.title;
      return newStep;
    });
  };

  // Build pages for Tabs and ensure there's always at least one tab
  const pages = getPagesFromSteps(sections || []);
  const pagesTabs = pages && pages.length ? pages : [{ name: 'Details', content: <></> }];

  // If selectedTab is out of range (e.g. sections changed), clamp it to 0
  useEffect(() => {
    if (selectedTab >= pagesTabs.length) {
      setSelectedTab(0);
    }
  }, [selectedTab, pagesTabs.length]);

  return (
    <Modal size="xl" isOpen={isOpen} fade={false} centered id="manage-devices-modal">
      <ModalBody>
        <Tabs
          classes="manage-devices-tabs"
          onTabsChange={setSelectedTab}
          selectedTab={selectedTab}
          pages={{
            tabs: pagesTabs.map((p, i) => ({
              ...p,
              content: React.cloneElement(p.content, { registerSave: registerSaveForIndex(i) })
            }))
          }}
        />
      </ModalBody>
      <ModalFooter>
        <CustomButton
          handleClick={() => {
            const cb = onCancel ? onCancel : toggle;
            cb();
          }}
          classes="btn-secondary"
          title="Cancel"
          disabled={false}
        />
        <CustomButton
          handleClick={() => {
            const reg = saveFunctionsRef.current[selectedTab];
            if (reg) {
              if (typeof reg === 'function') {
                reg();
              } else if (typeof reg.save === 'function') {
                reg.save();
              } else {
                onConfigChanged(device);
              }
            } else {
              onConfigChanged(device);
            }
          }}
          classes="btn-primary"
          title="Confirm changes"
          loading={loadingSubmit}
          disabled={canConfirm() || !changesMap[selectedTab]}
        />
      </ModalFooter>
    </Modal>
  );
};

export default DetailsModal;
